import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { Notification, NotificationData } from '../models/notification.model';
import { SupabaseService, REPLACE_UPCOMING_CHANGES, rpcFilter } from './supabase.service';
import { BusinessService } from './business.service';
import { take, switchMap } from "rxjs/operators";
import { UsersService } from "./users.service";
import { ViewAsService } from "./view-as.service";
import { Router } from "@angular/router";

@Injectable({
  providedIn: 'root'
})
export class NotificationService implements OnDestroy {
  private _notifications$ = new BehaviorSubject<Notification[]>([]);
  private _unreadCount$ = new BehaviorSubject<number>(0);
  private _loading$ = new BehaviorSubject<boolean>(false);
  private _error$ = new BehaviorSubject<string | null>(null);

  private readonly STORAGE_KEY = 'slick_notifications';
  private realtimeSubscription?: Subscription;

  // Public observables
  get notifications$(): Observable<Notification[]> {
    return this._notifications$.asObservable();
  }

  get unreadCount$(): Observable<number> {
    return this._unreadCount$.asObservable();
  }

  get loading$(): Observable<boolean> {
    return this._loading$.asObservable();
  }

  get error$(): Observable<string | null> {
    return this._error$.asObservable();
  }

  constructor(
    private supabaseService: SupabaseService,
    private businessService: BusinessService,
    private usersService: UsersService,
    private viewAsService: ViewAsService,
    private router: Router
  ) {}

  /**
   * Navigate from a notification with support for viewAs and fromUser contexts
   * This is shared logic used by both web and native notification handlers
   */
  async navigateFromNotification(actionUrl?: string, data?: NotificationData): Promise<void> {
    if (!actionUrl) {
      console.warn('No action URL provided for navigation');
      return;
    }

    try {
      // Handle viewAs context switching
      if (data?.viewAs && data.viewAs.length > 0) {
        const users = (await this.usersService.users$.pipe(take(1)).toPromise())!
            .filter(user => data.viewAs!.includes(user.id));
        console.log(users);

        this.viewAsService.changePrep$.next(users);
        await this.viewAsService.applyChanges(actionUrl);
      } else if (data?.fromUser) {
        const user = (await this.usersService.users$.pipe(take(1)).toPromise())!
            .find(user => user.id === data.fromUser!);

        if (user) {
          this.viewAsService.changePrep$.next([user]);
          await this.viewAsService.applyChanges(actionUrl);
        }
      }

      // Standard navigation without context switching
      await this.router.navigateByUrl(actionUrl);

    } catch (error) {
      console.error('Failed to navigate from notification:', error);
      // Fallback to simple navigation
      // try {
      //   await this.router.navigateByUrl(actionUrl);
      // } catch (fallbackError) {
      //   console.error('Fallback navigation also failed:', fallbackError);
      // }
    }
  }

  async initializeNotifications(): Promise<void> {
    // Load local notifications first
    const localNotifications = this.loadLocalNotifications();
    if (localNotifications.length > 0) {
      this._notifications$.next(localNotifications);
      this.updateUnreadCount();
    } else {
      // Initialize badge even with no local notifications
      this.updateNativeBadge(0);
    }

    // Setup real-time subscription
    this.setupRealtimeSubscription();
  }

  private loadLocalNotifications(): Notification[] {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        const notifications = JSON.parse(stored);
        // Convert date strings back to Date objects
        return notifications.map((n: any) => ({
          ...n,
          createdAt: new Date(n.createdAt),
          readAt: n.readAt ? new Date(n.readAt) : null
        }));
      }
    } catch (error) {
      console.error('Failed to load local notifications:', error);
    }
    return [];
  }

  private saveLocalNotifications(notifications: Notification[]): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(notifications));
    } catch (error) {
      console.error('Failed to save local notifications:', error);
    }
  }


  private async setupRealtimeSubscription(): Promise<void> {
    // Clean up existing subscription
    if (this.realtimeSubscription) {
      this.realtimeSubscription.unsubscribe();
    }

    // Get current user ID
    const userId = await this.getCurrentUserId();
    if (!userId) {
      console.warn('No user ID available for notifications subscription');
      return;
    }


    this.realtimeSubscription = this.businessService.selectedBusiness$.pipe(
      switchMap(business => {
        if(!business) {
          this.realtimeSubscription?.unsubscribe();
        }
        const filter = rpcFilter('user_id', 'eq', userId);
        return this.supabaseService.rpc<Notification[]>({
          cName: 'notifications_user_' + userId,
          schema: business!.businessId,
          fn: 'get_user_notifications',
          tables: [
            { table: 'notification', filter }
          ],
          options: { p_user_id: userId, p_limit: 100 }
        }, (changes, items) => {
          return REPLACE_UPCOMING_CHANGES(changes, items, true);
        });
      }),
      //   shareReplay(1)
    ).subscribe({
      next: (notifications) => {
        this._loading$.next(false);
        this._error$.next(null);

        if (notifications && notifications.length > 0) {
          this._notifications$.next(notifications);
          this.saveLocalNotifications(notifications);
          this.updateUnreadCount();
        } else {
          this._notifications$.next([]);
          this.updateUnreadCount();
        }
      },
      error: (error) => {
        console.error('Failed to subscribe to notifications:', error);
        this._error$.next('Failed to load notifications');
        this._loading$.next(false);
      }
    });
  }




  private async getCurrentUserId(): Promise<number | null> {
    try {
      const businessesAndUsers = await this.businessService.businessesAndUsers$.pipe(take(1)).toPromise();
      const selectedBusiness = await this.businessService.selectedBusiness$.pipe(take(1)).toPromise();
      
      if (businessesAndUsers?.data && selectedBusiness) {
        const currentBusiness = businessesAndUsers.data.find(
          item => item.businessData.businessId === selectedBusiness.businessId
        );
        return currentBusiness?.userData?.id || null;
      }
      return null;
    } catch (error) {
      console.error('Failed to get current user ID:', error);
      return null;
    }
  }

  private async getCurrentBusinessId(): Promise<string | null> {
    try {
      const selectedBusiness = await this.businessService.selectedBusiness$.pipe(take(1)).toPromise();
      return selectedBusiness?.businessId || null;
    } catch (error) {
      console.error('Failed to get current business ID:', error);
      return null;
    }
  }

  private updateUnreadCount(): void {
    const notifications = this._notifications$.value;
    const unreadCount = notifications.filter(n => !n.readAt).length;
    this._unreadCount$.next(unreadCount);
    
    // Update native app badge count
    this.updateNativeBadge(unreadCount);
  }

  private updateNativeBadge(count: number): void {
    try {
      if ((window as any).Pushy && (window as any).Pushy.setBadge) {
        (window as any).Pushy.setBadge(count);
      }
    } catch (error) {
      console.warn('Failed to update native badge count:', error);
    }
  }

  /**
   * Manually update the native app badge count
   * Can be called externally if needed
   */
  public updateBadgeCount(): void {
    const unreadCount = this._unreadCount$.value;
    this.updateNativeBadge(unreadCount);
  }

  async markAsRead(id: number): Promise<boolean> {
    try {
      // Update local state first
      const notifications = this._notifications$.value;
      const notification = notifications.find(n => n.id === id);
      
      if (notification && !notification.readAt) {
        notification.readAt = new Date();
        this._notifications$.next([...notifications]);
        this.saveLocalNotifications(notifications);
        this.updateUnreadCount();
      }
      
      // Update on server
      const businessId = await this.getCurrentBusinessId();

      if (!businessId) {
        console.error('Cannot mark as read. Missing business ID');
        return false;
      }

      await this.supabaseService.rpcFunc(
          businessId,
        'mark_notification_read',
        { p_notification_id: id }
      );
      
      return true;
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
      return false;
    }
  }

  async markAsReadByVisibility(id: number): Promise<boolean> {
    // Same as markAsRead for mock implementation
    return this.markAsRead(id);
  }

  async markAllAsRead(): Promise<boolean> {
    try {
      const notifications = this._notifications$.value;
      const updatedNotifications = notifications.map(n => ({
        ...n,
        readAt: n.readAt || new Date()
      }));
      
      this._notifications$.next(updatedNotifications);
      this.updateUnreadCount();
      
      return true;
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
      return false;
    }
  }

  async deleteNotification(id: number): Promise<boolean> {
    try {
      // Update local state first
      const notifications = this._notifications$.value;
      const filteredNotifications = notifications.filter(n => n.id !== id);
      
      this._notifications$.next(filteredNotifications);
      this.saveLocalNotifications(filteredNotifications);
      this.updateUnreadCount();
      
      // Update on server (soft delete)
      const businessId = await this.getCurrentBusinessId();
      const { error } = await this.supabaseService.supabase
        .from(businessId+'.NOTIFICATION')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', id);
      
      if (error) {
        console.error('Failed to delete notification on server:', error);
        // Could revert local change here if needed
      }
      
      return true;
    } catch (error) {
      console.error('Failed to delete notification:', error);
      return false;
    }
  }

  ngOnDestroy(): void {
    if (this.realtimeSubscription) {
      this.realtimeSubscription.unsubscribe();
    }
  }
}